import { useEffect, useRef, useCallback, useState } from 'react';
import { getAuth } from 'firebase/auth';

const WS_BASE  = process.env.REACT_APP_WORKER_WS_URL  || 'wss://celebrity-game.apihealhubspace.workers.dev';
const API_BASE = process.env.REACT_APP_WORKER_API_URL || 'https://celebrity-game.apihealhubspace.workers.dev';

// No more polling — WebSocket-first matchmaking
// Worker requests per game: 2 (lobby) + 2 (game) = 4 total
const QUEUE_TIMEOUT_MS = 60000;

export function useGameSocket({ onEvent }) {
  const ws           = useRef(null);  // active WebSocket (lobby or game)
  const lobbyWs      = useRef(null);  // lobby WebSocket during matchmaking
  const [status, setStatus] = useState('idle');
  const queueTimeout = useRef(null);
  const isMatchedRef = useRef(false);
  const onEventRef   = useRef(onEvent);
  const pingInterval = useRef(null);

  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  // ── Ping to keep game WebSocket alive ─────────────────────────────────────
  const stopPing = () => clearInterval(pingInterval.current);
  const startPing = (socket) => {
    pingInterval.current = setInterval(() => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 20000);
  };

  // ── Emit to game WebSocket ─────────────────────────────────────────────────
  const emit = useCallback((type, data = {}) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type, ...data }));
    }
  }, []);

  // ── Connect to game room WebSocket (unchanged from before) ─────────────────
  const connectToGame = useCallback(async (roomId) => {
    setStatus('connecting');
    try {
      const auth  = getAuth();
      const token = await auth.currentUser.getIdToken(true);
      const socket = new WebSocket(`${WS_BASE}/ws/${roomId}?token=${encodeURIComponent(token)}`);

      socket.onopen = () => {
        setStatus('matched');
        socket.send(JSON.stringify({ type: 'ready' }));
        startPing(socket);
      };
      socket.onmessage = (e) => {
        try { onEventRef.current?.(JSON.parse(e.data)); } catch {}
      };
      socket.onclose = (e) => {
        stopPing();
        if (e.code !== 1000) {
          setStatus('disconnected');
          onEventRef.current?.({ type: 'connection_lost' });
        } else {
          setStatus('idle');
        }
      };
      socket.onerror = () => {
        stopPing();
        setStatus('disconnected');
        onEventRef.current?.({ type: 'connection_error' });
      };

      ws.current = socket;
    } catch {
      setStatus('disconnected');
    }
  }, []);

  // ── Join queue via lobby WebSocket — ZERO HTTP POLLING ────────────────────
  const joinQueue = useCallback(async (streak = 0, name = 'Player') => {
    isMatchedRef.current = false;
    setStatus('waiting');

    // Hard timeout — if no match in 60s, give up
    queueTimeout.current = setTimeout(() => {
      if (!isMatchedRef.current) {
        // Close lobby WS cleanly
        try { lobbyWs.current?.close(1000, 'timeout'); } catch {}
        lobbyWs.current = null;
        setStatus('idle');
        onEventRef.current?.({ type: 'queue_timeout' });
      }
    }, QUEUE_TIMEOUT_MS);

    try {
      const auth  = getAuth();
      const token = await auth.currentUser.getIdToken(true);

      // Build lobby WebSocket URL with all params
      const lobbyUrl = new URL(`${WS_BASE}/ws/lobby`);
      lobbyUrl.searchParams.set('token',  token);
      lobbyUrl.searchParams.set('name',   name);
      lobbyUrl.searchParams.set('streak', String(streak));

      const lobby = new WebSocket(lobbyUrl.toString());
      lobbyWs.current = lobby;

      lobby.onmessage = async (e) => {
        try {
          const data = JSON.parse(e.data);

          if (data.type === 'match_found') {
            isMatchedRef.current = true;
            clearTimeout(queueTimeout.current);

            // Close lobby WS — we're done with it
            try { lobby.close(1000, 'matched'); } catch {}
            lobbyWs.current = null;

            // Fire match_found event to GamePage
            onEventRef.current?.({ type: 'match_found', ...data });

            // Connect to actual game room
            await connectToGame(data.roomId);
          } else if (data.type === 'queue_timeout') {
            clearTimeout(queueTimeout.current);
            try { lobby.close(1000, 'timeout'); } catch {}
            lobbyWs.current = null;
            setStatus('idle');
            onEventRef.current?.({ type: 'queue_timeout' });
          } else if (data.type === 'pong') {
            // ignore
          }
        } catch {}
      };

      lobby.onclose = (e) => {
        // If closed without matching and not intentional timeout
        if (!isMatchedRef.current && e.code !== 1000) {
          clearTimeout(queueTimeout.current);
          setStatus('idle');
          onEventRef.current?.({ type: 'connection_error' });
        }
      };

      lobby.onerror = () => {
        if (!isMatchedRef.current) {
          clearTimeout(queueTimeout.current);
          setStatus('idle');
          onEventRef.current?.({ type: 'connection_error' });
        }
      };

    } catch {
      clearTimeout(queueTimeout.current);
      setStatus('idle');
    }
  }, [connectToGame]);

  // ── Disconnect (cancel queue or leave game) ────────────────────────────────
  const disconnect = useCallback(() => {
    isMatchedRef.current = true;
    clearTimeout(queueTimeout.current);
    stopPing();

    // Close lobby WS if waiting
    try { lobbyWs.current?.close(1000, 'cancelled'); } catch {}
    lobbyWs.current = null;

    // Close game WS if in game
    try { ws.current?.close(1000); } catch {}
    ws.current = null;

    setStatus('idle');
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    clearTimeout(queueTimeout.current);
    stopPing();
    try { lobbyWs.current?.close(1000); } catch {}
    try { ws.current?.close(1000); } catch {}
  }, []);

  return { status, joinQueue, disconnect, emit };
}